import dayjs from "dayjs"
import advancedFormat from "dayjs/plugin/advancedFormat"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"
import React, { useState } from "react"

import { Button } from "./Buttons"
import NotificationDeliveryDetailsFlyout from "./NotificationDeliveryDetailsFlyout"

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)

const channelLabel = (ch) => {
  if (!ch) return "—"
  const map = {
    email: "Email",
    sms: "SMS",
    push_ios: "Push (iOS)",
    push_android: "Push (Android)",
    database: "In-app"
  }
  return map[ch] || ch
}

/** ISO timestamps from the API are formatted in the browser's local timezone. */
export function formatNotificationLogLocal(iso) {
  if (!iso) return "—"
  return dayjs(iso).format("MMM D, YYYY h:mm:ss A z")
}

const MESSAGE_PREVIEW_CHARS = 20

/** Single-line preview for the grid; full text stays in flyout / title tooltip. */
export function shortenNotificationMessagePreview(body) {
  const normalized = body.replace(/\s+/g, " ").trim()
  if (normalized.length <= MESSAGE_PREVIEW_CHARS) return normalized
  return `${normalized.slice(0, MESSAGE_PREVIEW_CHARS)}…`
}

/** Tooltip + emoji for notification_delivery_logs.status (unknown → ❓ + text tooltip). */
export function deliveryStatusDisplay(status) {
  const raw = status != null && status !== "" ? String(status).trim() : ""
  if (!raw) {
    return { emoji: null, tooltipText: "", empty: true }
  }
  const normalized = raw.toLowerCase()
  const emojiMap = {
    succeeded: "✅",
    failed: "❌",
    enqueued: "⏳"
  }
  const emoji = emojiMap[normalized] ?? "❓"
  const tooltipText = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase()
  return { emoji, tooltipText, empty: false }
}

function normalizeLogForFlyout(row) {
  return {
    body: row.body,
    bodyRedacted: row.bodyRedacted,
    createdAt: row.createdAt,
    deliveryChannel: row.deliveryChannel,
    durationMs: row.durationMs,
    enqueuedAt: row.enqueuedAt,
    errorCode: row.errorCode,
    errorMessage: row.errorMessage,
    eventType: row.eventType,
    finishedAt: row.finishedAt,
    fromAddress: row.fromAddress,
    startedAt: row.startedAt,
    status: row.status,
    subject: row.subject,
    toAddress: row.toAddress
  }
}

export default function NotificationDeliveryLogTable({ logs, emptyMessage = "No notification delivery history yet." }) {
  const [selected, setSelected] = useState(null)

  if (!logs || logs.length === 0) {
    return <div className="text-sm text-gray-dark">{emptyMessage}</div>
  }

  return (
    <>
      <div className="overflow-x-auto rounded-xl border border-gray-light">
        <table className="w-full min-w-[640px] table-fixed border-collapse text-left text-sm">
          <colgroup>
            <col className="w-[23%]" />
            <col className="w-[10%]" />
            <col className="w-[34%]" />
            <col className="w-[22ch]" />
            <col className="w-[9%]" />
            <col className="w-[11%]" />
          </colgroup>
          <thead className="bg-gray-ultralight text-xs font-semibold uppercase text-gray-dark">
            <tr>
              <th className="px-3 py-2">Sent</th>
              <th className="px-3 py-2">Channel</th>
              <th className="px-3 py-2">Type</th>
              <th className="w-[22ch] px-3 py-2">Message</th>
              <th className="px-3 py-2 text-center">Status</th>
              <th className="px-3 py-2 text-right">Details</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((row) => {
              const t = row.startedAt || row.createdAt
              const formatted = formatNotificationLogLocal(t)
              const previewFull = row.body ? row.body.replace(/\s+/g, " ").trim() : ""
              const previewShort = row.body ? shortenNotificationMessagePreview(row.body) : ""
              const statusUi = deliveryStatusDisplay(row.status)
              return (
                <tr key={row.id} className="border-t border-gray-light">
                  <td className="px-3 py-2 align-top text-gray-dark">{formatted}</td>
                  <td className="px-3 py-2 align-top">{channelLabel(row.deliveryChannel)}</td>
                  <td className="break-words px-3 py-2 align-top font-mono text-[10px] leading-snug text-gray-dark">
                    {row.eventType}
                  </td>
                  <td className="w-[22ch] max-w-[22ch] overflow-hidden px-3 py-2 align-top font-mono text-xs text-gray-dark">
                    {row.body ? (
                      <span className="block whitespace-nowrap" title={previewFull}>
                        {previewShort}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-3 py-2 text-center align-top">
                    {statusUi.empty ? (
                      <span className="inline-block text-gray-dark">—</span>
                    ) : (
                      <span
                        className="inline-flex cursor-default items-center justify-center text-lg leading-none"
                        title={statusUi.tooltipText}>
                        <span aria-hidden>{statusUi.emoji}</span>
                        <span className="sr-only">{statusUi.tooltipText}</span>
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right align-top">
                    <Button type="link" onClick={() => setSelected(normalizeLogForFlyout(row))}>
                      View
                    </Button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <NotificationDeliveryDetailsFlyout
        log={selected}
        visible={selected != null}
        onClose={() => setSelected(null)}
        formatLocal={formatNotificationLogLocal}
      />
    </>
  )
}
